<template>
  <div>
    <search-bar
      ref="searchBar"
      v-model="prompt"
      :loading-state="loadingState"
      :is-prompted="isPrompted"
      @submit="onPromptSubmit"
    />
    <div class="goals-list mt-4 mb-5">
      <goal-card-skeleton
        class="skeleton"
        v-if="
          loadingState === LoadingState.LOADING ||
            (loadingState === LoadingState.STREAMING && !hasGoalGenerationStarted)
        "
      />
      <template v-else-if="hasGoalGenerationStarted">
        <div class="d-flex justify-content-between align-items-center mb-2 px-3">
          <div>
            Sure, here are some OKRs for you...
          </div>
          <link-button
            class="p-0"
            underline
            @click="onPromptSubmit"
            v-if="loadingState === LoadingState.LOADED"
            >Try again</link-button
          >
        </div>
        <goal-template-row
          @select="$emit('select', $event)"
          class="mb-3 goal-template"
          v-for="(goal, index) in goals"
          :goal="goal"
          :key="index + goal.title"
        />
      </template>
      <div class="flex-center mt-5" v-else-if="loadingState === LoadingState.LOADED">
        <info-box>
          <template slot="message">
            There is currently no <b>Goals</b> to see here.
          </template>
        </info-box>
      </div>
      <div
        class="text-secondary flex-center mt-5"
        v-else-if="loadingState === LoadingState.ERRORED"
      >
        Something went wrong. <link-button @click="onPromptSubmit" underline>Try again</link-button>
      </div>
      <div class="flex-center my-5" ref="bottomPlaceholder">
        <link-button
          class="d-flex align-items-center"
          v-if="loadingState === LoadingState.STREAMING || loadingState === LoadingState.LOADING"
          @click="stopGenerating"
          underline
          >Stop generating <spinner class="ml-2" size="xs"
        /></link-button>
        <div
          class="d-flex justify-content-center align-items-center"
          v-else-if="loadingState === LoadingState.LOADED"
        >
          <link-button class="px-0 dot d-none d-md-block" underline @click="onPromptSubmit"
            >Try again</link-button
          >
          <link-button class="dot px-2" @click="typeNewQuestion" underline
            >New question</link-button
          >
          <search-helps class="d-none d-md-block" :on-prompt-selection="onPromptSelection" />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { trackAnalyticsEvent } from '@/analytics'
import LinkButton from '@/components/buttons/LinkButton.vue'
import InfoBox from '@/components/InfoBox'
import GoalCardSkeleton from '@/components/skeletons/GoalCardSkeleton'
import Spinner from '@/components/Spinner.vue'
import GoalTemplateRow from '@/components/templates/GoalTemplateRow'
import SearchHelps from '@/components/templates/SearchHelps.vue'
import { completeJSON } from '@/utils/jsonCompleter'
import SearchBar from '@/components/templates/SearchBar.vue'
import { EVENT_ACTION, EVENT_CATEGORY, EVENT_NAME } from '@/constants/analytics'
import { LoadingState } from '@/constants/common'
import { KR_MEASURING_UNIT } from '@/constants/kr'
import service from '@/service'
import urlManager from '@/urlManager'
import { snakeCaseToCamelCase } from '@/utils/objectCasingConvertors'
import safeJSONParse from '@/utils/safeJSONParse'
import showAPIError from '@/utils/showAPIError'
import * as R from 'ramda'

/**
 * This component shows the list of template goals
 */
export default {
  name: 'GoalAITemplates',
  props: ['filterData', 'orgId'],
  components: {
    SearchHelps,
    Spinner,
    LinkButton,
    SearchBar,
    GoalTemplateRow,
    InfoBox,
    GoalCardSkeleton
  },
  data() {
    return {
      loadingState: LoadingState.WAITING,
      LoadingState,
      prompt: '',
      isPrompted: false,
      tokens: '',
      backupGoals: '',
      controller: null
    }
  },
  computed: {
    goals() {
      try {
        if (!this.tokens) return []
        const getGoals = R.ifElse(Array.isArray, R.identity, R.prop('objectives'))
        const processGoalsResponse = R.compose(
          this.injectKrMeasureByInfo,
          snakeCaseToCamelCase,
          getGoals,
          safeJSONParse,
          completeJSON
        )
        return processGoalsResponse(this.tokens)
      } catch (e) {
        return this.backupGoals
      }
    },
    hasGoalGenerationStarted() {
      return !!this.goals?.[0]?.title
    }
  },
  methods: {
    onPromptSelection(prompt) {
      this.$refs.searchBar.onPromptSelection(prompt)
    },
    injectKrMeasureByInfo(goals) {
      const krData = {
        measureBy: KR_MEASURING_UNIT.PERCENTAGE,
        start: 0,
        end: 100
      }
      return goals.map(goal => ({
        ...goal,
        keyResults: goal.keyResults?.map(kr => ({ ...kr, ...krData }))
      }))
    },
    typeNewQuestion() {
      this.$refs.searchBar.selectTextArea()
    },
    cancelStream() {
      this.controller?.abort()
    },
    stopGenerating() {
      this.cancelStream()
      this.loadingState = LoadingState.LOADED
    },
    triggerRequest() {
      this.cancelStream() // this would cancel previous call if that is under progress
      this.tokens = ''
      this.backupGoals = []
      this.controller = new AbortController()

      const request = service.client({
        method: 'POST',
        onDownloadProgress: response => {
          const target = response.event.target
          this.tokens = target.response
          this.loadingState = LoadingState.STREAMING
        },
        url: urlManager.generateGoals(this.orgId),
        data: {
          prompt: this.prompt
        },
        responseType: 'stream',
        signal: this.controller.signal
      })
      this.request = request
      request
        .then(() => (this.loadingState = LoadingState.LOADED))
        .catch(payload => {
          if (!payload.config?.signal?.aborted) this.loadingState = LoadingState.ERRORED
        })
    },
    async onPromptSubmit() {
      try {
        trackAnalyticsEvent(EVENT_CATEGORY.ACTIVITY, EVENT_ACTION.SEARCH_NORTH_AI, EVENT_NAME.GOAL)
        this.$emit('prompt')
        this.isPrompted = true
        this.loadingState = LoadingState.LOADING
        this.triggerRequest()
      } catch (e) {
        showAPIError(e)
        this.loadingState = LoadingState.ERRORED
      }
    }
  },
  mounted() {},
  watch: {
    goals(newValue, oldValue) {
      this.backupGoals = newValue
      const isNewGoalStreamed =
        oldValue.length > 0 &&
        newValue.length > 0 &&
        newValue.length > oldValue.length &&
        this.loadingState === LoadingState.STREAMING
      if (isNewGoalStreamed) {
        this.$nextTick(() => {
          this.$refs.bottomPlaceholder.scrollIntoView({ behavior: 'smooth' })
        })
      }
    }
  }
}
</script>

<style lang="scss" scoped>
@import '~@/styles/_styleConfigurations.scss';
.goal-template {
  min-height: 260px;
}
.dot:after {
  @include dimension(4px, 4px);
  content: ' ';
  display: inline-block;
  background-color: $secondary;
  border-radius: 50%;
  margin-left: 8px;
  transform: translateY(-3px);
}
.skeleton {
  margin-top: 56px;
}
@include media-breakpoint-down(sm) {
  .dot:after {
    display: none;
  }
}
</style>
